import { useHandlePermissions } from "0.Mine/1.Hooks/security/permissions";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";
import { Event } from "0.Mine/3.Interfaces/Event";
import { UserLicense } from "0.Mine/3.Interfaces/license";
import { AddCircle, ChevronLeft, ChevronRight, Group } from "@mui/icons-material";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useState, useMemo, useEffect } from "react";

import EventCalendar from "../Container/EventCalendar";
import EventForm from "../Container/EventForm";
import EventModal from "../Container/EventModal";

const drawerWidth = 240;

interface User {
  id: string;
  username: string;
  color: string;
  email?: string;
  name: string;
}

interface BusinessCalendarUIProps {
  events: Event[];
  selectedEvent: Event | null;
  isModalOpen: boolean;
  isFormOpen: boolean;
  isEditMode: boolean;
  onSelectEvent: (event: Event) => void;
  onSelectSlot: ({ start, end }: { start: Date; end: Date }) => void;
  onCloseModal: () => void;
  onCloseForm: () => void;
  onEditEvent: () => void;
  onDeleteEvent: (id: string) => void;
  onSaveEvent: (event: Event) => void;
}

//DA RIFARE

export const BusinessCalendarUI: React.FC<BusinessCalendarUIProps> = ({
  events,
  selectedEvent,
  isModalOpen,
  isFormOpen,
  isEditMode,
  onSelectEvent,
  onSelectSlot,
  onCloseModal,
  onCloseForm,
  onEditEvent,
  onDeleteEvent,
  onSaveEvent,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [userLicenses, setUserLicenses] = useState<UserLicense[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showAllEvents, setShowAllEvents] = useState(true);

  const { session: userSession } = useSession();
  const { session } = useBusinessContext();
  const { getAllLicenses } = useHandlePermissions();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Ensure all dates are properly instantiated
  const sanitizeEventDates = (eventList: Event[]): Event[] => {
    return eventList.map((event) => ({
      ...event,
      start: event.start instanceof Date ? event.start : new Date(event.start || new Date()),
      end: event.end instanceof Date ? event.end : new Date(event.end || new Date()),
    }));
  };

  // Apply user-based filtering
  const filteredEvents = useMemo(() => {
    // If showing all events or if all users are selected, just return all events with sanitized dates
    if (showAllEvents || selectedUsers.length === users.length) {
      return sanitizeEventDates(events);
    }

    // Filter based on user selection
    const filtered = events.filter((event) => {
      // Case 1: Direct userId match (for mock data)
      if (event.userId && selectedUsers.includes(event.userId)) {
        return true;
      }

      // Case 2: Email-based sharing (for real data)
      if (Array.isArray(event.sharedWith) && event.sharedWith.length > 0) {
        // Get emails of all selected users
        const selectedUsersEmails = users
          .filter((user) => selectedUsers.includes(user.id))
          .map((user) => user.email)
          .filter(Boolean); // Remove any undefined emails

        // Check if any selected user's email is in the sharedWith array
        return event.sharedWith.some((sharedEmail) => selectedUsersEmails.includes(sharedEmail));
      }
      if (selectedUsers.includes(userSession.user.id)) {
        return true;
      }

      return false;
    });
    return sanitizeEventDates(filtered);
  }, [events, showAllEvents, selectedUsers, users, userSession.user.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!session.selectedBusiness?.id) return;

      // Initialize with current user
      const currentUser = {
        id: userSession.user.id,
        name: userSession.user.name,
        username: userSession.user.name,
        color: "#4CAF50",
        email: userSession.user.email,
      };

      setUsers([currentUser]);
      setSelectedUsers([currentUser.id]);

      // If user is a vendor, don't fetch additional users
      if (userSession.user.qlickup === "vendor") return;

      try {
        const response = await getAllLicenses(session.selectedBusiness.id);

        // Create complete user list including current user
        const allUsers = [
          {
            id: userSession.user.id,
            name: userSession.user.name,
            color: "#4CAF50",
            email: userSession.user.email,
            username: userSession.user.name,
          },
          ...response
            .filter((user) => user.id.toString() !== userSession.user.id)
            .map((user: UserLicense) => ({
              id: user.id.toString(),
              name: user.name,
              color: "#4CAF50",
              email: user.email,
              username: user.username,
            })),
        ];

        setUsers(allUsers);
        setSelectedUsers([userSession.user.id]);
        setUserLicenses(response);
      } catch (error) {
        console.error("Failed to fetch licenses:", error);
        // Fall back to just current user if request fails
        setUsers([currentUser]);
      }
    };

    fetchData();
  }, [session, userSession]);

  if (!session) {
    return <p>Loading...</p>;
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    // Disable "show all" when specific users are selected
    setShowAllEvents(false);
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.id));
    }
    setShowAllEvents(false);
  };

  const handleSelectEvent = (event: Event) => {
    try {
      if (!event?.id) {
        console.error("Invalid event object:", event);
        return;
      }

      const sanitizedEvent = {
        ...event,
        start: event.start instanceof Date ? event.start : new Date(event.start || new Date()),
        end: event.end instanceof Date ? event.end : new Date(event.end || new Date()),
      };

      onSelectEvent(sanitizedEvent);
    } catch (error) {
      console.error("Error selecting event:", error);
    }
  };

  if (isMobileOrTablet) {
    return (
      <Box sx={{ width: "100%", minHeight: "calc(100vh - 64px)" }}>
        <Box
          sx={{
            width: "100%",
            px: 2,
            py: 1,
            background: "#eef0f7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            borderRadius: "10px",
            gap: 1,
          }}
        >
          <Box
            sx={{
              borderRadius: "99px",
              background: "linear-gradient(310deg, #36376d, #5a6eb4)",
              color: "#ffffff",
              fontSize: "0.75rem",
              padding: "0.5rem 2rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
            onClick={() =>
              onSelectSlot({
                start: new Date(),
                end: new Date(new Date().getTime() + 3600000),
              })
            }
          >
            <AddCircle sx={{ mr: 1, fontSize: 20 }} />
            Crea Evento
          </Box>

          <FormControl sx={{ minWidth: 180 }}>
            <Select
              multiple
              value={selectedUsers}
              onChange={(e) => {
                const value = e.target.value;
                const newValue = typeof value === "string" ? value.split(",") : value;

                // Se è selezionato "all", mostra tutti e imposta showAllEvents
                if (newValue.includes("all")) {
                  toggleAllUsers();
                } else {
                  setSelectedUsers(newValue);
                  setShowAllEvents(false);
                }
              }}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === users.length) return "Tutti gli utenti";
                return users
                  .filter((u) => selected.includes(u.id))
                  .map((u) => u.name)
                  .join(", ");
              }}
            >
              <MenuItem key="all" value="all">
                <Checkbox
                  checked={selectedUsers.length === users.length}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                />
                <ListItemText primary="Tutti gli utenti" />
              </MenuItem>

              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={selectedUsers.includes(user.id)} />
                  <ListItemText primary={user.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mx: "-16px", pt: 2 }}>
          <EventCalendar
            events={filteredEvents}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={onSelectSlot}
          />
        </Box>

        {isModalOpen && selectedEvent && (
          <EventModal
            event={selectedEvent}
            onClose={onCloseModal}
            onEdit={onEditEvent}
            onDelete={onDeleteEvent}
          />
        )}

        {isFormOpen && selectedEvent && (
          <EventForm
            event={selectedEvent}
            isEdit={isEditMode}
            onSave={onSaveEvent}
            onCancel={onCloseForm}
            users={userLicenses}
          />
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: "calc(100vh - 64px)" }}>
      <Drawer
        variant="persistent"
        anchor="left"
        open={sidebarOpen}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            position: "static",
            height: "auto",
            background: "#eef0f7",
            padding: "40px 5px",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            position: "absolute",
            top: "63px",
            left: "15px",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IconButton onClick={toggleSidebar}>
            <ChevronLeft />
          </IconButton>
        </Box>

        <Box
          sx={{
            marginLeft: "36px",
            marginRight: "16px",
            marginTop: "-24px",
            marginBottom: "16px",
            borderRadius: "99px",
            background: "linear-gradient(310deg, #36376d, #5a6eb4)",
            color: "#ffffff",
            fontSize: "0.75rem",
            padding: "0.5rem 2rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() =>
            onSelectSlot({
              start: new Date(),
              end: new Date(new Date().getTime() + 3600000),
            })
          }
        >
          <AddCircle sx={{ mr: 1, fontSize: 20 }} />
          Crea Evento
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <ListItem dense>
            <ListItemIcon sx={{ minWidth: "26px" }}>
              <Checkbox
                edge="start"
                checked={selectedUsers.length === users.length}
                indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                onChange={toggleAllUsers}
                sx={{ borderRadius: "99px" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Group sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body1">Tutto lo staff</Typography>
                </Box>
              }
            />
          </ListItem>

          <List dense>
            {users.map((user) => (
              <ListItem key={user.id} dense>
                <ListItemIcon sx={{ minWidth: "26px" }}>
                  <Checkbox
                    edge="start"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUser(user.id)}
                    sx={{ borderRadius: "99px" }}
                  />
                </ListItemIcon>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: user.color,
                    mr: 1,
                  }}
                />
                <ListItemText primary={user.name} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: "margin 0.2s",
          marginLeft: sidebarOpen ? 0 : `-${drawerWidth}px`,
          paddingLeft: sidebarOpen ? 2 : 0,
          width: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Toggle button (visible when sidebar is closed) */}
        {!sidebarOpen && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              position: "absolute",
              left: 10,
              top: 10,
              zIndex: 1000,
            }}
          >
            <ChevronRight />
          </IconButton>
        )}

        <main className="flex-grow pt-4" style={{ padding: "30px" }}>
          <EventCalendar
            events={filteredEvents}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={onSelectSlot}
          />
        </main>

        {/* Event Modal */}
        {isModalOpen && selectedEvent && (
          <EventModal
            event={selectedEvent}
            onClose={onCloseModal}
            onEdit={onEditEvent}
            onDelete={onDeleteEvent}
          />
        )}

        {/* Event Form */}
        {isFormOpen && selectedEvent && (
          <EventForm
            event={selectedEvent}
            isEdit={isEditMode}
            onSave={onSaveEvent}
            onCancel={onCloseForm}
            users={userLicenses}
          />
        )}
      </Box>
    </Box>
  );
};
